/* eslint-env browser */
// OPC 点位曲线导出 PDF（验收 #10）：离屏 A4 版式（OpcCurvePdfSheet，794×1123 px ≈ A4 @96dpi）→ html2canvas → jsPDF A4 竖版。
// 中文在 html2canvas 里已变成像素，jsPDF 不需要嵌中文字体。
// html2canvas / jspdf 已被 main.js 里的 utils/htmlToPdf.js 打进主包，这里直接静态引入不额外增加体积。
import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';

/** A4 竖版版面（CSS px，96dpi：210mm × 297mm） */
export const SHEET_WIDTH_PX = 794;
export const SHEET_HEIGHT_PX = 1123;
/** 版面左右边距（px）；曲线图宽 = 版面宽 - 2 × 边距 */
export const SHEET_MARGIN_X_PX = 48;
export const SHEET_CHART_HEIGHT_PX = 580;

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const HOST_ATTR = 'data-opc-curve-pdf-host';

// Windows / macOS 文件名不允许的字符与控制字符
// eslint-disable-next-line no-control-regex
const ILLEGAL_FILE_CHARS = /[\\/:*?"<>|\u0000-\u001f]/g;

function fileSegment(text) {
    return String(text === null || text === undefined ? '' : text).trim().replace(ILLEGAL_FILE_CHARS, '_');
}

/**
 * 文件名 `<设备编码>_<工单号>-<工序号>_<middle>.pdf`。
 * 工单号 / 工序号都空时省略该段；只填了一个时只写那一个。非法文件名字符替换为 `_`。
 */
export function buildPdfFileName({deviceCode, orderCode, operationSeq, middle}) {
    const key = [fileSegment(orderCode), fileSegment(operationSeq)].filter(Boolean).join('-');
    return [fileSegment(deviceCode), key, fileSegment(middle)].filter(Boolean).join('_') + '.pdf';
}

function pad2(n) {
    return n < 10 ? '0' + n : '' + n;
}

/** Date → yyyy-MM-dd HH:mm[:ss]（浏览器本地时间） */
export function formatDateTime(date, withSeconds) {
    const text = date.getFullYear() + '-' + pad2(date.getMonth() + 1) + '-' + pad2(date.getDate())
        + ' ' + pad2(date.getHours()) + ':' + pad2(date.getMinutes());
    return withSeconds ? text + ':' + pad2(date.getSeconds()) : text;
}

/** [Date, Date] → `yyyy-MM-dd HH:mm ~ yyyy-MM-dd HH:mm`；不合法返回空串 */
export function formatRange(range) {
    if (!Array.isArray(range) || !(range[0] instanceof Date) || !(range[1] instanceof Date)) {
        return '';
    }
    return formatDateTime(range[0]) + ' ~ ' + formatDateTime(range[1]);
}

/** 文本 trim 后为空返回 ''（PDF 版面据此画手写下划线） */
export function textOrEmpty(value) {
    return value === null || value === undefined ? '' : String(value).trim();
}

/**
 * 在 body 下建一个屏幕外的容器放版面（fixed 定位：不受弹窗 transform、页面滚动影响）。
 * 截图时在克隆文档里把它挪回原点（见 renderSheetToPdf 的 onclone）。用完调用方 removeChild。
 */
export function createOffscreenHost() {
    const host = document.createElement('div');
    host.setAttribute(HOST_ATTR, '');
    host.style.cssText = 'position:fixed;left:-20000px;top:0;width:' + SHEET_WIDTH_PX + 'px;'
        + 'height:' + SHEET_HEIGHT_PX + 'px;overflow:hidden;pointer-events:none;';
    document.body.appendChild(host);
    return host;
}

/** 等 el 里的图片都加载完（data URL 一般已同步可用，兜底等 onload / onerror） */
export function waitForImages(el) {
    const images = Array.prototype.slice.call(el.querySelectorAll('img'));
    return Promise.all(images.map(img => {
        if (img.complete && img.naturalWidth > 0) {
            return Promise.resolve();
        }
        return new Promise(resolve => {
            img.addEventListener('load', resolve, {once: true});
            img.addEventListener('error', resolve, {once: true});
        });
    }));
}

/**
 * 版面元素 → A4 竖版 PDF 并下载。
 * @param el       已挂在 createOffscreenHost() 容器里的版面根元素（宽高 = SHEET_WIDTH_PX × SHEET_HEIGHT_PX）
 * @param fileName 下载文件名（buildPdfFileName）
 * @param title    PDF 文档属性里的标题（可空）
 */
export function renderSheetToPdf(el, fileName, title) {
    return waitForImages(el).then(() => {
        return html2canvas(el, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            logging: false,
            // 原节点在屏幕外；克隆文档里挪回视口原点再截，避免截到空白
            onclone: doc => {
                const host = doc.querySelector('[' + HOST_ATTR + ']');
                if (host) {
                    host.style.left = '0px';
                    host.style.top = '0px';
                }
            }
        }).then(canvas => {
            const pdf = new JsPDF({orientation: 'p', unit: 'mm', format: 'a4', compress: true});
            if (title) {
                pdf.setProperties({title});
            }
            // 版面按 A4 比例画的，等宽铺满；万一高度超出则按高度缩放
            let width = A4_WIDTH_MM;
            let height = A4_WIDTH_MM * canvas.height / canvas.width;
            if (height > A4_HEIGHT_MM) {
                width = width * A4_HEIGHT_MM / height;
                height = A4_HEIGHT_MM;
            }
            pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', (A4_WIDTH_MM - width) / 2, 0, width, height);
            pdf.save(fileName);
        });
    });
}
